import { useEffect, useState } from 'react';

import { Center, Header, Spinner, Show, Paragraph } from '@vimeo/bokeh';
import { ClipPageBase } from '@vimeo/clip-ui/components/ClipLayout/components/ClipPageBase';
import { GRID_TEMPLATE_AREAS_LOADER } from '@vimeo/clip-ui/components/ClipLayout/utils';
import { ModuleWrapper } from '@vimeo/clip-ui/components/ModuleWrapper';
import { PasswordForm } from '@vimeo/clip-ui/components/PasswordForm/PasswordForm';
import { PlayerWrapper } from '@vimeo/clip-ui/components/PlayerWrapper';
import { translate } from '@vimeo/i18n-runtime';
import { ShowcasePlaylistSkeleton } from '@vimeo/viewer-showcase-ui/components/ShowcasePlaylist';

import { useValidateShowcasePassword } from '../hooks/use-validate-showcase-password';

export const PasswordPage = ({
  isCookiesDisabled = false,
  onSuccess,
}: {
  isCookiesDisabled?: boolean;
  onSuccess: VoidFunction;
}) => {
  const [isInvalid, setIsInvalid] = useState(false);
  const { validatePassword, isSuccess, error, loading, called } = useValidateShowcasePassword({
    isCookiesDisabled,
  });

  useEffect(() => {
    if (isSuccess) {
      onSuccess();
    }
  }, [isSuccess, onSuccess]);

  useEffect(() => {
    if (called && error) {
      setIsInvalid(true);
    }
  }, [called, error, setIsInvalid]);

  const shouldShowLoading = loading || isSuccess; // should show loading state on submit or during post-authentication page reload

  return (
    <ClipPageBase gridTemplateAreas={GRID_TEMPLATE_AREAS_LOADER}>
      <PlayerWrapper gridArea="media">
        <Center w="100%" h="100%">
          {shouldShowLoading ? (
            <Spinner size="xl" />
          ) : (
            <PasswordForm error={error} isInvalid={isInvalid} onSubmit={validatePassword}>
              <Header as="h1" size={{ base: 'lg', md: '2xl' }} textAlign="center">
                {translate('This showcase is private')}
              </Header>
              <Paragraph fontSize={{ base: 'text-sm', md: 'text' }} textAlign="center">
                {translate(
                  'Enter the password to view it. You can also try logging in or contacting the creator to gain access.',
                )}
              </Paragraph>
            </PasswordForm>
          )}
        </Center>
      </PlayerWrapper>

      <ModuleWrapper gridArea="contentStack" backgroundColor="background">
        <Show above="md">
          <ShowcasePlaylistSkeleton length={4} />
        </Show>

        <Show below="md">
          <ShowcasePlaylistSkeleton length={1} />
        </Show>
      </ModuleWrapper>
    </ClipPageBase>
  );
};
